import type {
  AiAgent,
  AiAssistantApproval as AiAssistantApprovalProto,
  AiAssistantBinding as AiAssistantBindingProto,
  AiAssistantBindingOverrides as AiAssistantBindingOverridesProto,
  AiAssistantPolicy as AiAssistantPolicyProto,
  AiAssistantPromptVersion as AiAssistantPromptVersionProto,
  AiAssistantQuotas as AiAssistantQuotasProto,
  AiAssistantTemplate as AiAssistantTemplateProto,
  AiAssistantUsage as AiAssistantUsageProto,
  AiModel,
  AiProvider,
  AiTool,
  AiUserProfile,
  AssistantAssignment as AssistantAssignmentProto,
  Entity,
  Role,
} from '@zdc/sdk';
import { getSDK } from '@/store/sdkStore';
import type {
  AssistantApproval,
  AssistantAssignment,
  AssistantAssignmentInput,
  AssistantBinding,
  AssistantBindingInput,
  AssistantBindingOverrides,
  AssistantChannel,
  AssistantDefaults,
  AssistantMemoryScope,
  AssistantPolicy,
  AssistantPromptVersion,
  AssistantPromptVersionInput,
  AssistantQuota,
  AssistantSource,
  AssistantTemplate,
  AssistantTemplateInput,
  AssistantToolOption,
  AssistantUsage,
  MyAssistantAvailability,
  MyAssistantOverview,
} from './types';
import { keyRefFromMode, keyModeFromRef } from './types';

const MEMORY_SCOPES: ReadonlyArray<AssistantMemoryScope> = ['', 'global', 'session', 'per_user'];
const SOURCES: ReadonlyArray<AssistantSource> = ['binding', 'assignment', 'system', 'requested'];

function memoryScope(value: string): AssistantMemoryScope {
  const found = MEMORY_SCOPES.find((scope) => scope === value);
  if (!found && value !== '') {
    throw new Error(`Сервер вернул неизвестный режим памяти ассистента: «${value}».`);
  }
  return found || '';
}

function source(value: string): AssistantSource {
  const found = SOURCES.find((item) => item === value);
  if (!found) {
    throw new Error(`Сервер вернул неизвестный источник ассистента: «${value}».`);
  }
  return found;
}

function quotaFromProto(q: AiAssistantQuotasProto | undefined): AssistantQuota {
  return {
    maxTokensPerDay: q ? q.maxTokensPerDay : 0,
    maxRequestsPerHour: q ? q.maxRequestsPerHour : 0,
    maxCostCentsPerDay: q ? Number(q.maxCostCentsPerDay) : 0,
    maxContextTokens: q ? q.maxContextTokens : 0,
  };
}

// int64 в proto (центы) ↔ number в UI. Отрицательные/нечисловые → 0: форма
// допускает только неотрицательные значения, сервер валидирует повторно.
function centsToBigInt(value: number): bigint {
  if (!Number.isFinite(value) || value < 0) return BigInt(0);
  return BigInt(Math.trunc(value));
}

function quotaToProto(q: AssistantQuota) {
  return {
    maxTokensPerDay: q.maxTokensPerDay,
    maxRequestsPerHour: q.maxRequestsPerHour,
    maxCostCentsPerDay: centsToBigInt(q.maxCostCentsPerDay),
    maxContextTokens: q.maxContextTokens,
  };
}

function policyFromProto(p: AiAssistantPolicyProto | undefined): AssistantPolicy {
  return {
    userCanOverrideModel: p ? p.userCanOverrideModel : false,
    userCanEditStyle: p ? p.userCanEditStyle : false,
    userCanEditMemory: p ? p.userCanEditMemory : false,
    approvalTtlMinutes: p ? p.approvalTtlMinutes : 0,
  };
}

function policyToProto(p: AssistantPolicy) {
  return {
    userCanOverrideModel: p.userCanOverrideModel,
    userCanEditStyle: p.userCanEditStyle,
    userCanEditMemory: p.userCanEditMemory,
    approvalTtlMinutes: p.approvalTtlMinutes,
  };
}

function channelsFromProto(values: readonly string[]): AssistantChannel[] {
  return [...values];
}

function templateFromProto(t: AiAssistantTemplateProto): AssistantTemplate {
  return {
    id: t.id,
    code: t.code,
    name: t.name,
    description: t.description,
    agentCode: t.agentCode,
    providerId: t.providerId,
    defaultModelId: t.defaultModelId,
    modelMapping: { ...t.modelMapping },
    stylePresets: t.stylePresets.map((preset: any) => ({ code: preset.code, name: preset.name, tone: preset.tone })),
    basePrompt: t.basePrompt,
    guardPrompt: t.guardPrompt,
    tools: [...t.toolsAllowlist],
    toolsRestricted: t.toolsAllowlistSet,
    destructiveTools: [...t.destructiveTools],
    memoryScope: memoryScope(t.memoryScope),
    memoryTtlDays: t.memoryTtlDays,
    allowUserWipe: t.allowUserWipe,
    quotas: quotaFromProto(t.quotas),
    policy: policyFromProto(t.policy),
    channels: channelsFromProto(t.channels),
    isEnabled: t.isActive,
    isPublic: t.isPublic,
    isSystem: t.isSystem,
    isDefaultForNewUsers: t.isDefaultForNewUsers,
    templateId: t.templateId,
    version: t.version,
    updatedAt: t.updatedAt,
  };
}

function promptVersionFromProto(v: AiAssistantPromptVersionProto): AssistantPromptVersion {
  return {
    id: v.id,
    templateCode: v.templateCode,
    version: v.version,
    basePrompt: v.basePrompt,
    guardPrompt: v.guardPrompt,
    rolloutPct: v.rolloutPct,
    note: v.note,
    publishedBy: v.publishedBy,
    publishedAt: v.publishedAt,
    createdAt: v.createdAt,
  };
}

function overridesFromProto(o: AiAssistantBindingOverridesProto | undefined): AssistantBindingOverrides {
  return {
    modelId: o ? o.modelId : '',
    style: o ? o.style : '',
    memoryScope: memoryScope(o ? o.memoryScope : ''),
    channels: channelsFromProto(o ? o.channels : []),
    language: o ? o.language : '',
  };
}

function overridesToProto(o: AssistantBindingOverrides) {
  return {
    modelId: o.modelId,
    style: o.style,
    memoryScope: o.memoryScope,
    channels: [...o.channels],
    language: o.language,
  };
}

function bindingFromProto(b: AiAssistantBindingProto): AssistantBinding {
  return {
    id: b.id,
    userId: b.userId,
    templateCode: b.templateCode,
    isDefault: b.isDefault,
    enabled: b.enabled,
    overrides: overridesFromProto(b.overrides),
    quotaOverrides: quotaFromProto(b.quotaOverrides),
    assignedBy: b.assignedBy,
    createdAt: b.createdAt,
    updatedAt: b.updatedAt,
  };
}

function usageFromProto(u: AiAssistantUsageProto): AssistantUsage {
  return {
    usageDate: u.usageDate,
    usageHour: u.usageHour,
    tokensIn: u.tokensIn,
    tokensOut: u.tokensOut,
    costCents: Number(u.costCents),
    requests: u.requests,
  };
}

function approvalFromProto(a: AiAssistantApprovalProto): AssistantApproval {
  return {
    id: a.id,
    userId: a.userId,
    templateCode: a.templateCode,
    bindingId: a.bindingId,
    toolCode: a.toolCode,
    args: a.args,
    status: a.status,
    requestedAt: a.requestedAt,
    decidedAt: a.decidedAt,
    decidedBy: a.decidedBy,
    expiresAt: a.expiresAt,
  };
}

// ----------------------------- Шаблоны -----------------------------

export async function listAssistantTemplates(): Promise<AssistantTemplate[]> {
  const response = await getSDK().ai.listAssistantTemplates({});
  return response.items.map(templateFromProto);
}

export async function getAssistantTemplate(input: { id?: string; code?: string }): Promise<AssistantTemplate> {
  return templateFromProto(await getSDK().ai.getAssistantTemplate({
    id: input.id || '',
    code: input.code || '',
  }));
}

export async function saveAssistantTemplate(input: AssistantTemplateInput): Promise<AssistantTemplate> {
  const saved = await getSDK().ai.saveAssistantTemplate({
    code: input.code,
    name: input.name,
    description: input.description,
    agentCode: input.agentCode,
    providerId: input.providerId,
    defaultModelId: input.defaultModelId,
    modelMapping: { ...input.modelMapping },
    basePrompt: input.basePrompt,
    guardPrompt: input.guardPrompt,
    stylePresets: input.stylePresets.map((preset) => ({
      code: preset.code,
      name: preset.name,
      tone: preset.tone,
    })),
    toolsAllowlist: [...input.tools],
    toolsAllowlistSet: input.toolsRestricted,
    destructiveTools: [...input.destructiveTools],
    memoryScope: input.memoryScope,
    memoryTtlDays: input.memoryTtlDays,
    allowUserWipe: input.allowUserWipe,
    channels: [...input.channels],
    quotas: quotaToProto(input.quotas),
    policy: policyToProto(input.policy),
    isActive: input.isEnabled,
    isPublic: input.isPublic,
  });
  return templateFromProto(saved);
}

export async function deleteAssistantTemplate(code: string): Promise<void> {
  await getSDK().ai.deleteAssistantTemplate({ code });
}

// ----------------------------- Ассистент по умолчанию -----------------------------

export async function getAssistantDefaults(): Promise<AssistantDefaults> {
  const response = await getSDK().ai.getAssistantDefaults({});
  return {
    defaultTemplateCode: response.defaultTemplateCode,
    allowSystemDefault: response.allowSystemDefault,
  };
}

export async function saveAssistantDefaults(input: AssistantDefaults): Promise<AssistantDefaults> {
  const response = await getSDK().ai.setAssistantDefaults({
    defaultTemplateCode: input.defaultTemplateCode,
    allowSystemDefault: input.allowSystemDefault,
  });
  return {
    defaultTemplateCode: response.defaultTemplateCode,
    allowSystemDefault: response.allowSystemDefault,
  };
}

// ----------------------------- История промта -----------------------------

export async function listAssistantPromptVersions(templateCode: string): Promise<AssistantPromptVersion[]> {
  const response = await getSDK().ai.listAssistantPromptVersions({ templateCode });
  return response.items.map(promptVersionFromProto);
}

export async function publishAssistantPromptVersion(input: AssistantPromptVersionInput): Promise<AssistantPromptVersion> {
  return promptVersionFromProto(await getSDK().ai.publishAssistantPromptVersion({
    templateCode: input.templateCode,
    basePrompt: input.basePrompt,
    guardPrompt: input.guardPrompt,
    rolloutPct: input.rolloutPct,
    note: input.note,
  }));
}

export async function rollbackAssistantPromptVersion(templateCode: string, version: number): Promise<AssistantTemplate> {
  return templateFromProto(await getSDK().ai.rollbackAssistantPromptVersion({ templateCode, version }));
}

// ----------------------------- Привязки -----------------------------

export async function listAssistantBindings(userId = ''): Promise<AssistantBinding[]> {
  const response = await getSDK().ai.listAssistantBindings({ userId });
  return response.items.map(bindingFromProto);
}

export async function saveAssistantBinding(input: AssistantBindingInput): Promise<AssistantBinding> {
  const saved = await getSDK().ai.saveAssistantBinding({
    userId: input.userId,
    templateCode: input.templateCode,
    isDefault: input.isDefault,
    enabled: input.enabled,
    overrides: overridesToProto(input.overrides),
    quotaOverrides: quotaToProto(input.quotaOverrides),
  });
  return bindingFromProto(saved);
}

export async function deleteAssistantBinding(id: string): Promise<void> {
  await getSDK().ai.deleteAssistantBinding({ id });
}

export async function bulkAssignAssistantBindings(
  userIds: string[],
  input: AssistantBindingInput,
): Promise<number> {
  const response = await getSDK().ai.bulkAssignAssistantBindings({
    userIds: [...userIds],
    templateCode: input.templateCode,
    isDefault: input.isDefault,
    enabled: input.enabled,
    overrides: overridesToProto(input.overrides),
    quotaOverrides: quotaToProto(input.quotaOverrides),
  });
  return response.assigned;
}

// ----------------------------- Назначения ассистента (P1) -----------------------------

function assignmentFromProto(a: AssistantAssignmentProto): AssistantAssignment {
  return {
    id: a.id,
    scope: a.scope,
    scopeId: a.scopeId,
    agentCode: a.agentCode,
    modelId: a.modelId,
    style: a.style,
    providerId: a.providerId,
    keyRef: a.keyRef,
    dailyCostLimit: a.dailyCostLimit,
    isEnabled: a.isEnabled !== false,
    validTo: a.validTo,
    createdBy: a.createdBy,
    createdAt: a.createdAt,
    updatedAt: a.updatedAt,
  };
}

/** Дата ГГГГ-ММ-ДД → RFC3339 (конец дня в локальном поясе); пусто = бессрочно. */
function validToFromInput(validFromDate: string): string {
  const day = validFromDate.trim();
  if (!day) return '';
  const at = new Date(`${day}T23:59:59`);
  if (Number.isNaN(at.getTime())) {
    throw new Error(`Некорректная дата срока действия: «${day}».`);
  }
  return at.toISOString();
}

export async function listAssistantAssignments(): Promise<AssistantAssignment[]> {
  const response = await getSDK().ai.listAssistantAssignments({});
  return response.items.map(assignmentFromProto);
}

export async function saveAssistantAssignment(input: AssistantAssignmentInput): Promise<AssistantAssignment> {
  const saved = await getSDK().ai.setAssistantAssignment({
    id: input.id,
    scope: input.scope,
    scopeId: input.scopeId,
    agentCode: input.agentCode,
    modelId: input.modelId,
    style: input.style,
    providerId: input.providerId,
    keyRef: keyRefFromMode(input.keyMode, input.scopeId),
    dailyCostLimit: input.dailyCostLimit,
    isEnabled: input.isEnabled,
    validTo: validToFromInput(input.validFromDate),
  });
  return assignmentFromProto(saved);
}

export async function deleteAssistantAssignment(id: string): Promise<void> {
  await getSDK().ai.deleteAssistantAssignment({ id });
}

// ----------------------------- Мой ассистент -----------------------------

export async function getMyAssistantAvailability(): Promise<MyAssistantAvailability> {
  const response = await getSDK().ai.getMyAssistantAvailability({});
  return {
    available: response.available,
    reason: response.reason,
    source: response.source,
    templateCode: response.templateCode,
    hasBinding: response.hasBinding,
    agentCode: response.agentCode,
    modelId: response.modelId,
    providerId: response.providerId,
  };
}

export async function getMyAssistantOverview(): Promise<MyAssistantOverview> {
  const response = await getSDK().ai.getMyAssistant({});
  return {
    source: source(response.source),
    template: response.template ? templateFromProto(response.template) : null,
    binding: response.binding ? bindingFromProto(response.binding) : null,
    agentCode: response.agentCode,
    agentName: response.agentName,
    modelId: response.modelId,
    modelName: response.modelName,
    providerId: response.providerId,
    quotas: quotaFromProto(response.quotas),
    channels: channelsFromProto(response.channels),
    usageToday: response.usageToday ? usageFromProto(response.usageToday) : null,
    pendingApprovals: response.pendingApprovals.map(approvalFromProto),
  };
}

export async function listMyAssistantApprovals(): Promise<AssistantApproval[]> {
  const response = await getSDK().ai.listMyAssistantApprovals({});
  return response.items.map(approvalFromProto);
}

export async function decideMyAssistantApproval(id: string, approved: boolean): Promise<AssistantApproval> {
  return approvalFromProto(await getSDK().ai.decideMyAssistantApproval({ id, approved }));
}

// ----------------------------- Справочники для форм -----------------------------

export async function listAssistantAgents(): Promise<AiAgent[]> {
  const response = await getSDK().ai.listAiAgents({});
  return response.items;
}

export async function listAssistantModels(): Promise<AiModel[]> {
  const response = await getSDK().ai.listAiModels({});
  return response.items;
}

export async function listAssistantTools(): Promise<AssistantToolOption[]> {
  const response = await getSDK().ai.listAiTools({});
  return response.items.map((tool: AiTool) => ({
    code: tool.code,
    name: tool.name,
    description: tool.description,
  }));
}

export async function listAssistantUsers(): Promise<Entity[]> {
  const response = await getSDK().entity.listEntities({});
  return response.items;
}

/** Провайдеры ИИ (для выбора провайдера назначения; ownerId = личный). */
export async function listAssistantProviders(): Promise<AiProvider[]> {
  const response = await getSDK().ai.listAiProviders({});
  return response.items;
}

/** Роли базы (для выбора роли назначения). */
export async function listAssistantRoles(): Promise<Role[]> {
  const response = await getSDK().auth.getRoles({});
  return response.items;
}

export async function loadMyAssistantProfile(): Promise<AiUserProfile> {
  return getSDK().ai.getUserProfile({});
}

export async function saveMyAssistantProfile(profile: AiUserProfile): Promise<AiUserProfile> {
  return getSDK().ai.saveUserProfile({
    userId: profile.userId,
    summary: profile.summary,
    goals: profile.goals,
    preferences: profile.preferences,
    topics: profile.topics,
    desires: profile.desires,
    needs: profile.needs,
    hobbies: profile.hobbies,
    activities: profile.activities,
    style: profile.style,
    character: profile.character,
    pets: profile.pets,
    relatives: profile.relatives,
    acquaintances: profile.acquaintances,
    friends: profile.friends,
  });
}

// Память личного ассистента вынесена в отдельный типизированный адаптер
// (lib/assistant/memoryAdapter.ts): приватность и API очистки — там.

export function channelLabel(value: string): string {
  return value === 'chat' ? 'Внутренний чат'
    : value === 'telegram' ? 'Telegram'
      : value === 'max' ? 'MAX'
        : value === 'email' ? 'Электронная почта'
          : value === 'webpush' ? 'Уведомления на устройстве'
            : value === 'in_app' ? 'В приложении'
              : value;
}
